import { Navigate, Route, Routes } from 'react-router-dom';
import type { Role } from '@hrlabs/shared-types';
import { useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Login } from './routes/Login';
import { Dashboard } from './routes/Dashboard';
import { JobList } from './routes/jobs/List';
import { JobEdit } from './routes/jobs/Edit';
import { BlogList } from './routes/blog/List';
import { BlogEdit } from './routes/blog/Edit';
import { ServiceList } from './routes/services/List';
import { ServiceEdit } from './routes/services/Edit';
import { TeamList } from './routes/team/List';
import { TeamEdit } from './routes/team/Edit';
import { LogoList } from './routes/logos/List';
import { TestimonialList } from './routes/testimonials/List';
import { TestimonialEdit } from './routes/testimonials/Edit';
import { UserList } from './routes/users/List';
import { UserEdit } from './routes/users/Edit';
import { SettingsIndex } from './routes/settings/Index';
import { JobManagement } from './routes/settings/JobManagement';
import { AiTranslation } from './routes/settings/AiTranslation';

function NoAccess() {
  const { user, logout } = useAuth();
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div className="card p-4 shadow-sm text-center" style={{ width: 380 }}>
        <h5 className="fw-bold mb-2">No admin access</h5>
        <p className="text-muted small mb-3">
          You are signed in as <strong>{user?.email}</strong>, but this account has no admin
          role yet. Ask an administrator to grant you access, then sign in again.
        </p>
        <button className="btn btn-outline-secondary" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function RequireRole({ allow, children }: { allow: Role[]; children: React.ReactNode }) {
  const { user, role, loading } = useAuth();
  if (loading) return <div className="p-5 text-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!role) return <NoAccess />;
  if (!allow.includes(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const editor: Role[] = ['admin', 'moderator'];
const adminOnly: Role[] = ['admin'];

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <RequireRole allow={editor}>
            <Layout />
          </RequireRole>
        }
      >
        <Route index element={<Dashboard />} />

        <Route path="jobs" element={<JobList />} />
        <Route path="jobs/new" element={<JobEdit />} />
        <Route path="jobs/:id" element={<JobEdit />} />

        <Route path="blog" element={<BlogList />} />
        <Route path="blog/new" element={<BlogEdit />} />
        <Route path="blog/:slug" element={<BlogEdit />} />

        <Route
          path="services"
          element={<RequireRole allow={adminOnly}><ServiceList /></RequireRole>}
        />
        <Route
          path="services/new"
          element={<RequireRole allow={adminOnly}><ServiceEdit /></RequireRole>}
        />
        <Route
          path="services/:id"
          element={<RequireRole allow={adminOnly}><ServiceEdit /></RequireRole>}
        />

        <Route path="team" element={<RequireRole allow={adminOnly}><TeamList /></RequireRole>} />
        <Route path="team/new" element={<RequireRole allow={adminOnly}><TeamEdit /></RequireRole>} />
        <Route path="team/:id" element={<RequireRole allow={adminOnly}><TeamEdit /></RequireRole>} />

        <Route path="logos" element={<RequireRole allow={adminOnly}><LogoList /></RequireRole>} />

        <Route
          path="testimonials/:kind"
          element={<RequireRole allow={adminOnly}><TestimonialList /></RequireRole>}
        />
        <Route
          path="testimonials/:kind/new"
          element={<RequireRole allow={adminOnly}><TestimonialEdit /></RequireRole>}
        />
        <Route
          path="testimonials/:kind/:id"
          element={<RequireRole allow={adminOnly}><TestimonialEdit /></RequireRole>}
        />

        <Route path="users" element={<RequireRole allow={adminOnly}><UserList /></RequireRole>} />
        <Route path="users/new" element={<RequireRole allow={adminOnly}><UserEdit /></RequireRole>} />
        <Route path="users/:uid" element={<RequireRole allow={adminOnly}><UserEdit /></RequireRole>} />

        <Route path="settings" element={<RequireRole allow={adminOnly}><SettingsIndex /></RequireRole>} />
        <Route
          path="settings/job-management"
          element={<RequireRole allow={adminOnly}><JobManagement /></RequireRole>}
        />
        <Route
          path="settings/ai-translation"
          element={<RequireRole allow={adminOnly}><AiTranslation /></RequireRole>}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
